import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 머리글 ▾ 값 거르기(레거시 그리드처럼 — 2026-10-02 함남열). 누르면 그 칸의 값 목록이 뜨고
 * 체크한 값만 남긴다. 순수 규칙은 `lib/colFilter.ts`.
 *
 * - 목록(`getValues`)은 **열 때** 만든다 — 3,700줄 표에서 칸마다 늘 세면 입력마다 느려진다.
 * - 창은 `document.body` 에 띄운다(fixed) — 표를 감싼 스크롤 상자 안에 두면 잘린다.
 * - 「전체 선택」 상태로 확인하면 거르기를 푼다(`undefined`) — 「전부 고름」과 「안 거름」을 같은 뜻으로.
 */
export default function ColumnFilter({
  label, selected, getValues, onApply, className = '',
}: {
  label: string;
  selected: string[] | undefined;
  getValues: () => { value: string; count: number }[];
  onApply: (sel: string[] | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const active = selected !== undefined;
  return (
    <>
      <button
        ref={btn}
        type="button"
        title={active ? `${label} — 거르는 중(${selected!.length}개 값)` : `${label} 값으로 거르기`}
        className={`ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded px-0.5 align-middle text-[10px] leading-none ${
          active ? 'bg-brand-600 text-white' : 'text-ink-faint hover:bg-canvas hover:text-ink'} ${className}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        ▾
      </button>
      {open && btn.current && (
        <FilterPopup
          anchor={btn.current}
          label={label}
          selected={selected}
          values={getValues()}
          onClose={() => setOpen(false)}
          onApply={(s) => { onApply(s); setOpen(false); }}
        />
      )}
    </>
  );
}

function FilterPopup({
  anchor, label, selected, values, onClose, onApply,
}: {
  anchor: HTMLElement; label: string; selected: string[] | undefined;
  values: { value: string; count: number }[];
  onClose: () => void; onApply: (sel: string[] | undefined) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [term, setTerm] = useState('');
  // 거르는 중이 아니면 전부 체크된 상태로 연다(엑셀과 같다).
  const [on, setOn] = useState<Set<string>>(() => new Set(selected ?? values.map((v) => v.value)));
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const w = 240;
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)), top: r.bottom + 4 });
  }, [anchor]);

  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (box.current?.contains(e.target as Node) || anchor.contains(e.target as Node)) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [anchor, onClose]);

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase();
    return t ? values.filter((v) => v.value.toLowerCase().includes(t)) : values;
  }, [term, values]);
  const allShownOn = shown.length > 0 && shown.every((v) => on.has(v.value));

  function toggleShown() {
    const n = new Set(on);
    for (const v of shown) { if (allShownOn) n.delete(v.value); else n.add(v.value); }
    setOn(n);
  }

  function apply() {
    // 검색어로 좁혀 놓고 확인하면 「보이는 값 중 체크한 것」만 — 엑셀의 검색 거르기와 같다.
    const pickedVals = (term.trim() ? shown : values).map((v) => v.value).filter((v) => on.has(v));
    onApply(!term.trim() && pickedVals.length === values.length ? undefined : pickedVals);
  }

  return createPortal(
    <div ref={box} style={{ left: pos.left, top: pos.top }}
         className="fixed z-[60] w-60 rounded-lg border border-line bg-surface p-2 text-xs shadow-lg"
         onClick={(e) => e.stopPropagation()}>
      <div className="mb-1 font-semibold">{label}</div>
      <input className="field mb-1 py-1 text-xs" placeholder="값 찾기" autoFocus
             value={term} onChange={(e) => setTerm(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') apply(); }} />
      <label className="flex items-center gap-1.5 border-b border-line px-1 py-1 font-medium">
        <input type="checkbox" checked={allShownOn} onChange={toggleShown} />
        {term.trim() ? '찾은 값 전체' : '전체 선택'}
        <span className="ml-auto text-ink-faint">{shown.length.toLocaleString('ko-KR')}개</span>
      </label>
      <ul className="max-h-64 overflow-y-auto py-1">
        {shown.map((v) => (
          <li key={v.value}>
            <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 hover:bg-canvas">
              <input type="checkbox" checked={on.has(v.value)}
                     onChange={() => setOn((s) => { const n = new Set(s); if (n.has(v.value)) n.delete(v.value); else n.add(v.value); return n; })} />
              <span className="truncate" title={v.value}>{v.value}</span>
              <span className="ml-auto pl-1 text-ink-faint">{v.count.toLocaleString('ko-KR')}</span>
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="px-1 py-1 text-ink-faint">없음</li>}
      </ul>
      <div className="flex gap-1 border-t border-line pt-1.5">
        <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => onApply(undefined)}>거르기 풀기</button>
        <button type="button" className="btn-primary ml-auto px-3 py-1 text-xs" onClick={apply}>확인</button>
      </div>
    </div>,
    document.body,
  );
}
